/**
 * UserDetail - Comprehensive user detail view for customer support
 * 
 * Displays:
 * - Cognito attributes and account status
 * - Uploaded documents from S3
 * - Conversation history
 * - Settings and preferences
 * - Subscription status
 * - Mastery tracking data
 * 
 * SuperAdmin actions:
 * - Edit user attributes
 * - Disable/enable account (with reason)
 * - Reset password
 * - All actions logged via Audit_Logger
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAdminAuth } from '../auth/useAdminAuth';
import AdminCache from '../components/shared/AdminCache';
import SkeletonLoaderComponents from '../components/shared/SkeletonLoader';
import StatusBadge from '../components/common/StatusBadge';
import Modal from '../components/common/Modal';
import { API_ENDPOINTS } from '../config/api-endpoints';
import SubscriptionEndDatePanel from '../components/subscription/SubscriptionEndDatePanel';
import UpgradeHistoryPanel from '../components/subscription/UpgradeHistoryPanel';
import './UserDetail.css';

const { Skeleton } = SkeletonLoaderComponents;

const UserDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAdminAuth();
  
  const [user, setUser] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [masteryData, setMasteryData] = useState(null);
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Modals
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [showEnableModal, setShowEnableModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [expandedDocId, setExpandedDocId] = useState(null);
  const [selectedConvId, setSelectedConvId] = useState(null);

  const isSuperAdmin = currentUser?.isSuperAdmin || false;

  // Fetch user details
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const session = await fetchAuthSession();
        // CRITICAL: Use idToken (not accessToken) for admin authorization
        // cognito:groups claim is ONLY in ID token, NOT in access token
        const token = session.tokens?.idToken?.toString();

        // Fetch user attributes
        const userResponse = await fetch(`${API_ENDPOINTS.ADMIN.USER_MANAGEMENT}/users/${userId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!userResponse.ok) throw new Error('Failed to fetch user');
        const userData = await userResponse.json();
        
        // DEBUG: Log the user data structure to verify attributes
        console.log('[UserDetail] User data structure:', {
          hasAttributes: !!userData.user?.attributes,
          attributeKeys: userData.user?.attributes ? Object.keys(userData.user.attributes) : [],
          sampleAttributes: userData.user?.attributes ? {
            tier: userData.user.attributes['custom:tier'],
            accountType: userData.user.attributes['custom:account_type'],
            age: userData.user.attributes['custom:age']
          } : null,
          fullUser: userData.user
        });
        
        setUser(userData.user);

        // Use documents and conversations from the user management endpoint
        // (these are fetched server-side from DocumentStore and ConversationSessions)
        if (userData.documents) {
          setDocuments(userData.documents);
          console.log('[UserDetail] Documents loaded from user endpoint:', userData.documents.length);
        }
        if (userData.conversations) {
          setChatHistory(userData.conversations);
          console.log('[UserDetail] Conversations loaded from user endpoint:', userData.conversations.length);
        }

        // Fetch chat history from dedicated endpoint as fallback (if not returned above)
        if (!userData.conversations) {
          try {
            console.log('[UserDetail] Fetching chat history from dedicated endpoint for:', userId);
            const chatResponse = await fetch(`${API_ENDPOINTS.CHAT.HISTORY}?userId=${userId}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            if (chatResponse.ok) {
              const chatData = await chatResponse.json();
              setChatHistory(chatData.conversations || []);
            }
          } catch (err) {
            console.warn('[UserDetail] Failed to load chat history from fallback:', err);
          }
        }

        // Fetch uploaded documents from dedicated endpoint as fallback
        if (!userData.documents) {
          try {
            const docsResponse = await fetch(`${API_ENDPOINTS.FILES.FILE_MANAGER}?userId=${userId}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            if (docsResponse.ok) {
              const docsData = await docsResponse.json();
              setDocuments(docsData.files || []);
            }
          } catch (err) {
            console.warn('[UserDetail] Failed to load documents from fallback:', err);
          }
        }

        // Fetch mastery data
        try {
          const masteryResponse = await fetch(`${API_ENDPOINTS.ANALYTICS.PROGRESS}?userId=${userId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          if (masteryResponse.ok) {
            const masteryDataResult = await masteryResponse.json();
            setMasteryData(masteryDataResult.data);
          }
        } catch (err) {
          console.warn('Failed to load mastery data:', err);
        }

        // Fetch promo codes used by this user
        try {
          const promoResponse = await fetch(`${API_ENDPOINTS.ADMIN.PROMO_CODE}/admin/promo-codes?userEmail=${encodeURIComponent(userData.user?.email || '')}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          if (promoResponse.ok) {
            const promoData = await promoResponse.json();
            // Filter to only codes redeemed by this user
            const userPromoCodes = (promoData.promoCodes || []).filter(pc => {
              const redeemers = Array.isArray(pc.redeemedBy) ? pc.redeemedBy : [];
              return redeemers.some(r => r.userEmail === userData.user?.email || r.userId === userId);
            });
            setPromoCodes(userPromoCodes);
          }
        } catch (err) {
          console.warn('Failed to load promo codes:', err);
        }

        // Cache user data
        AdminCache.set(`user_${userId}`, userData.user, AdminCache.TTL.medium);
      } catch (err) {
        console.error('[UserDetail] Error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [userId]);

  // Update user attributes
  const handleUpdateUser = async (updates) => {
    try {
      setActionLoading(true);
      const session = await fetchAuthSession();
      // CRITICAL: Use idToken (not accessToken) for admin authorization
      // cognito:groups claim is ONLY in ID token, NOT in access token
      const token = session.tokens?.idToken?.toString();

      const response = await fetch(`${API_ENDPOINTS.ADMIN.USER_MANAGEMENT}/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) throw new Error('Failed to update user');

      const data = await response.json();
      setUser(data.user);
      setShowEditModal(false);
      
      // Log to audit
      await logAuditAction('USER_UPDATED', { userId, updates });
    } catch (err) {
      console.error('Error updating user:', err);
      alert('Failed to update user: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Disable user
  const handleDisableUser = async (reason) => {
    try {
      setActionLoading(true);
      const session = await fetchAuthSession();
      // CRITICAL: Use idToken (not accessToken) for admin authorization
      // cognito:groups claim is ONLY in ID token, NOT in access token
      const token = session.tokens?.idToken?.toString();

      const response = await fetch(`${API_ENDPOINTS.ADMIN.USER_MANAGEMENT}/users/${userId}/disable`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason })
      });

      if (!response.ok) throw new Error('Failed to disable user');

      const data = await response.json();
      setUser(data.user);
      setShowDisableModal(false);
      
      // Log to audit
      await logAuditAction('USER_DISABLED', { userId, reason });
    } catch (err) {
      console.error('Error disabling user:', err);
      alert('Failed to disable user: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Enable user
  const handleEnableUser = async () => {
    try {
      setActionLoading(true);
      const session = await fetchAuthSession();
      // CRITICAL: Use idToken (not accessToken) for admin authorization
      // cognito:groups claim is ONLY in ID token, NOT in access token
      const token = session.tokens?.idToken?.toString();

      const response = await fetch(`${API_ENDPOINTS.ADMIN.USER_MANAGEMENT}/users/${userId}/enable`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to enable user');

      const data = await response.json();
      setUser(data.user);
      setShowEnableModal(false);
      
      // Log to audit
      await logAuditAction('USER_ENABLED', { userId });
    } catch (err) {
      console.error('Error enabling user:', err);
      alert('Failed to enable user: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Reset password
  const handleResetPassword = async () => {
    try {
      setActionLoading(true);
      const session = await fetchAuthSession();
      // CRITICAL: Use idToken (not accessToken) for admin authorization
      // cognito:groups claim is ONLY in ID token, NOT in access token
      const token = session.tokens?.idToken?.toString();

      console.log('[UserDetail] Sending reset password request for:', userId);
      console.log('[UserDetail] Token present:', !!token);

      const response = await fetch(`${API_ENDPOINTS.ADMIN.USER_MANAGEMENT}/users/${userId}/reset-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('[UserDetail] Reset password response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        console.error('[UserDetail] Reset password failed:', errorData);
        throw new Error(errorData.message || `Failed to reset password (${response.status})`);
      }

      const data = await response.json();
      console.log('[UserDetail] Reset password success:', data);

      setShowResetModal(false);
      alert('Password reset email sent successfully');
      
      // Log to audit
      await logAuditAction('PASSWORD_RESET', { userId });
    } catch (err) {
      console.error('[UserDetail] Error resetting password:', err);
      alert(`Failed to reset password: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Log audit action
  const logAuditAction = async (action, details) => {
    try {
      const session = await fetchAuthSession();
      // CRITICAL: Use idToken (not accessToken) for admin authorization
      // cognito:groups claim is ONLY in ID token, NOT in access token
      const token = session.tokens?.idToken?.toString();

      await fetch(API_ENDPOINTS.ADMIN.AUDIT_LOGGER, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action,
          targetType: 'user',
          targetId: userId,
          details,
          timestamp: new Date().toISOString()
        })
      });
    } catch (err) {
      console.error('Failed to log audit action:', err);
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status config
  const getStatusConfig = (status, enabled) => {
    if (!enabled) return { text: 'Disabled', variant: 'danger' };
    
    const statusMap = {
      'CONFIRMED': { text: 'Active', variant: 'success' },
      'UNCONFIRMED': { text: 'Unconfirmed', variant: 'warning' },
      'FORCE_CHANGE_PASSWORD': { text: 'Password Reset', variant: 'info' }
    };
    
    return statusMap[status] || { text: status, variant: 'default' };
  };

  if (loading) {
    return (
      <div className="user-detail">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} width="100%" height="100px" className="admin-skeleton-gap" />
        ))}
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="user-detail">
        <div className="admin-alert admin-alert--danger">
          <span>❌</span> {error || 'User not found'}
        </div>
        <button onClick={() => navigate('/users')} className="admin-btn admin-btn--secondary">
          ← Back to Users
        </button>
      </div>
    );
  }

  const statusConfig = getStatusConfig(user.status, user.enabled);

  return (
    <div className="user-detail">
      {/* Header */}
      <div className="user-detail__header">
        <button onClick={() => navigate('/users')} className="user-detail__back-btn">
          ← Back to Users
        </button>
        <div className="user-detail__title-row">
          <div className="user-detail__avatar-large">
            {(user.name || user.email || '?')[0].toUpperCase()}
          </div>
          <div>
            <h2 className="user-detail__title">{user.name || 'No name'}</h2>
            <p className="user-detail__subtitle">{user.email}</p>
          </div>
          <StatusBadge text={statusConfig.text} variant={statusConfig.variant} />
        </div>
      </div>

      {/* Tabs */}
      <div className="admin-tabs" role="tablist">
        <button
          role="tab"
          aria-selected={activeTab === 'overview'}
          className={`admin-tab ${activeTab === 'overview' ? 'admin-tab--active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'documents'}
          className={`admin-tab ${activeTab === 'documents' ? 'admin-tab--active' : ''}`}
          onClick={() => setActiveTab('documents')}
        >
          Documents<span className="admin-tab__badge">{documents.length}</span>
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'conversations'}
          className={`admin-tab ${activeTab === 'conversations' ? 'admin-tab--active' : ''}`}
          onClick={() => setActiveTab('conversations')}
        >
          Conversations<span className="admin-tab__badge">{chatHistory.length}</span>
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'mastery'}
          className={`admin-tab ${activeTab === 'mastery' ? 'admin-tab--active' : ''}`}
          onClick={() => setActiveTab('mastery')}
        >
          Mastery Data
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'subscription'}
          className={`admin-tab ${activeTab === 'subscription' ? 'admin-tab--active' : ''}`}
          onClick={() => setActiveTab('subscription')}
        >
          Subscription
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'promo-codes'}
          className={`admin-tab ${activeTab === 'promo-codes' ? 'admin-tab--active' : ''}`}
          onClick={() => setActiveTab('promo-codes')}
        >
          Promo Codes<span className="admin-tab__badge">{promoCodes.length}</span>
        </button>
      </div>

      {/* Tab Content */}
      <div className="user-detail__content">
        {activeTab === 'overview' && (
          <div className="admin-grid">
            {/* Signup Progress Tracker */}
            <div className="admin-card">
              <div className="admin-card-header">
                <h3 className="admin-card-title">📋 Signup Progress</h3>
                {user.signupProgress && (
                  <StatusBadge 
                    text={user.signupProgress.isComplete ? 'Complete' : user.signupProgress.stageLabel} 
                    variant={user.signupProgress.isComplete ? 'success' : 'warning'} 
                  />
                )}
              </div>
              {user.signupProgress ? (
                <div className="user-detail__signup-progress">
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                    {/* Required: Account Created */}
                    <div style={{
                      flex: '1 1 0', minWidth: '120px', padding: '0.75rem', borderRadius: '0.5rem', textAlign: 'center',
                      background: user.signupProgress.details.accountCreated ? '#ECFDF5' : '#FEF2F2',
                      border: `1px solid ${user.signupProgress.details.accountCreated ? '#10B981' : '#EF4444'}`
                    }}>
                      <div style={{ fontSize: '1.25rem' }}>{user.signupProgress.details.accountCreated ? '✅' : '❌'}</div>
                      <div style={{ fontSize: '0.75rem', fontWeight: 600, marginTop: '0.25rem' }}>Account Created</div>
                    </div>
                    {/* Required: Email Confirmed */}
                    <div style={{
                      flex: '1 1 0', minWidth: '120px', padding: '0.75rem', borderRadius: '0.5rem', textAlign: 'center',
                      background: user.signupProgress.details.emailConfirmed ? '#ECFDF5' : '#FEF2F2',
                      border: `1px solid ${user.signupProgress.details.emailConfirmed ? '#10B981' : '#EF4444'}`
                    }}>
                      <div style={{ fontSize: '1.25rem' }}>{user.signupProgress.details.emailConfirmed ? '✅' : '❌'}</div>
                      <div style={{ fontSize: '0.75rem', fontWeight: 600, marginTop: '0.25rem' }}>Email Confirmed</div>
                    </div>
                    {/* Required: Payment */}
                    <div style={{
                      flex: '1 1 0', minWidth: '120px', padding: '0.75rem', borderRadius: '0.5rem', textAlign: 'center',
                      background: user.signupProgress.details.paymentCompleted ? '#ECFDF5' : '#FEF2F2',
                      border: `1px solid ${user.signupProgress.details.paymentCompleted ? '#10B981' : '#EF4444'}`
                    }}>
                      <div style={{ fontSize: '1.25rem' }}>{user.signupProgress.details.paymentCompleted ? '✅' : '❌'}</div>
                      <div style={{ fontSize: '0.75rem', fontWeight: 600, marginTop: '0.25rem' }}>Payment</div>
                      <div style={{ fontSize: '0.65rem', color: '#6B7280' }}>
                        {(user.cognitoAttributes?.['custom:tier'] || user.attributes?.['custom:tier'] || 'free')}
                      </div>
                    </div>
                    {/* Optional: Setup (not shown for child accounts) */}
                    {user.signupProgress.details.setupCompleted !== null && (
                      <div style={{
                        flex: '1 1 0', minWidth: '120px', padding: '0.75rem', borderRadius: '0.5rem', textAlign: 'center',
                        background: user.signupProgress.details.setupCompleted ? '#ECFDF5' : '#F9FAFB',
                        border: `1px solid ${user.signupProgress.details.setupCompleted ? '#10B981' : '#D1D5DB'}`
                      }}>
                        <div style={{ fontSize: '1.25rem' }}>{user.signupProgress.details.setupCompleted ? '✅' : '➖'}</div>
                        <div style={{ fontSize: '0.75rem', fontWeight: 600, marginTop: '0.25rem' }}>Learning Profile</div>
                        <div style={{ fontSize: '0.65rem', color: '#9CA3AF' }}>optional</div>
                      </div>
                    )}
                    {/* Required (family only): Student Created */}
                    {user.signupProgress.isFamilyPlan && (
                      <div style={{
                        flex: '1 1 0', minWidth: '120px', padding: '0.75rem', borderRadius: '0.5rem', textAlign: 'center',
                        background: user.signupProgress.details.studentCreated ? '#ECFDF5' : '#FEF2F2',
                        border: `1px solid ${user.signupProgress.details.studentCreated ? '#10B981' : '#EF4444'}`
                      }}>
                        <div style={{ fontSize: '1.25rem' }}>{user.signupProgress.details.studentCreated ? '✅' : '❌'}</div>
                        <div style={{ fontSize: '0.75rem', fontWeight: 600, marginTop: '0.25rem' }}>Student Created</div>
                      </div>
                    )}
                  </div>
                  {!user.signupProgress.isComplete && (
                    <div style={{ 
                      padding: '0.5rem 0.75rem', 
                      background: '#FEF3C7', 
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      color: '#92400E'
                    }}>
                      ⚠️ Signup incomplete — <strong>{user.signupProgress.stageLabel}</strong>
                      {' '}({user.signupProgress.requiredCompleted}/{user.signupProgress.requiredTotal} required steps done)
                    </div>
                  )}
                  {user.signupProgress.hasAttributeMismatches && (
                    <div style={{ 
                      padding: '0.5rem 0.75rem', 
                      background: '#FEE2E2', 
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      color: '#991B1B',
                      marginTop: '0.5rem'
                    }}>
                      🔧 <strong>Attribute Mismatches Detected:</strong>
                      <ul style={{ margin: '0.25rem 0 0 1.25rem', padding: 0 }}>
                        {user.signupProgress.attributeMismatches.map((msg, i) => (
                          <li key={i}>{msg}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <span className="user-detail__no-data">Signup progress data not available</span>
              )}
            </div>

            {/* Account — core identity */}
            <div className="admin-card">
              <div className="admin-card-header">
                <h3 className="admin-card-title">👤 Account</h3>
                {isSuperAdmin && (
                  <button onClick={() => setShowEditModal(true)} className="admin-btn admin-btn--secondary admin-btn--sm">
                    Edit
                  </button>
                )}
              </div>
              <div className="user-detail__info-grid">
                <div className="user-detail__info-item">
                  <label>Name</label>
                  <span>{user.name || 'Not set'}</span>
                </div>
                <div className="user-detail__info-item">
                  <label>Email</label>
                  <span>{user.email}</span>
                </div>
                <div className="user-detail__info-item">
                  <label>Account Type</label>
                  <span>{user.attributes?.['custom:account_type'] || 'individual'}</span>
                </div>
                <div className="user-detail__info-item" style={!user.emailVerified ? { border: '1px solid #EF4444', background: '#FEF2F2' } : {}}>
                  <label>Email Verified</label>
                  <span>{user.emailVerified ? '✅ Yes' : '❌ No'}</span>
                </div>
                <div className="user-detail__info-item">
                  <label>Created</label>
                  <span>{formatDate(user.createdAt)}</span>
                </div>
                <div className="user-detail__info-item">
                  <label>Groups</label>
                  <span>
                    {user.groups?.length > 0 ? (
                      <span style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                        {user.groups.map(g => (
                          <span key={g} className={`user-detail__group-badge ${
                            g === 'SuperAdmins' ? 'user-detail__group-badge--super' :
                            g === 'Admins' ? 'user-detail__group-badge--admin' : ''
                          }`}>{g}</span>
                        ))}
                      </span>
                    ) : 'None'}
                  </span>
                </div>
                {user.phoneNumber && (
                  <div className="user-detail__info-item">
                    <label>Phone</label>
                    <span>{user.phoneNumber}</span>
                  </div>
                )}
                {user.attributes?.['custom:oauth_provider'] && user.attributes['custom:oauth_provider'] !== 'Cognito' && (
                  <div className="user-detail__info-item">
                    <label>OAuth Provider</label>
                    <span>{user.attributes['custom:oauth_provider']}</span>
                  </div>
                )}
                <div className="user-detail__info-item">
                  <label>User ID</label>
                  <span className="user-detail__mono">{user.userId}</span>
                </div>
              </div>
            </div>

            {/* Subscription & Billing — highlight if payment stage is stuck */}
            {(() => {
              const tier = user.attributes?.['custom:tier'] || 'free';
              const subStatus = user.attributes?.['custom:subscription_status'];
              const isFree = tier === 'free';
              const paymentStuck = !isFree && subStatus !== 'active';
              const hasPaymentData = user.attributes?.['custom:last_payment_date'] || user.attributes?.['custom:subscription_id'];
              const hasSubEndDate = user.attributes?.['custom:sub_end_date'];
              const isVip = hasSubEndDate && !hasPaymentData;
              return (
                <div className="admin-card" style={paymentStuck ? { border: '2px solid #EF4444' } : {}}>
                  <div className="admin-card-header">
                    <h3 className="admin-card-title">💳 Subscription & Billing</h3>
                    {paymentStuck && <StatusBadge text="Payment Issue" variant="danger" />}
                  </div>
                  <div className="user-detail__info-grid">
                    <div className="user-detail__info-item" style={paymentStuck ? { border: '1px solid #EF4444', background: '#FEF2F2' } : {}}>
                      <label>Tier</label>
                      <span>{tier}</span>
                    </div>
                    <div className="user-detail__info-item" style={paymentStuck ? { border: '1px solid #EF4444', background: '#FEF2F2' } : {}}>
                      <label>Status</label>
                      <span>{subStatus || 'N/A'}</span>
                    </div>
                    {user.attributes?.['custom:plan_type'] && (
                      <div className="user-detail__info-item">
                        <label>Plan Type</label>
                        <span>{user.attributes['custom:plan_type']}</span>
                      </div>
                    )}
                    {user.attributes?.['custom:sub_end_date'] && (
                      <div className="user-detail__info-item">
                        <label>Subscription End</label>
                        <span>{formatDate(user.attributes['custom:sub_end_date'])}</span>
                      </div>
                    )}
                    {user.attributes?.['custom:trial_status'] && (
                      <div className="user-detail__info-item">
                        <label>Trial Status</label>
                        <span>{user.attributes['custom:trial_status']}</span>
                      </div>
                    )}
                    {user.attributes?.['custom:last_payment_date'] && (
                      <div className="user-detail__info-item">
                        <label>Last Payment</label>
                        <span>{formatDate(user.attributes['custom:last_payment_date'])}</span>
                      </div>
                    )}
                    {user.subscription?.planId && (
                      <div className="user-detail__info-item">
                        <label>Plan ID</label>
                        <span>{user.subscription.planId}</span>
                      </div>
                    )}
                    {isSuperAdmin && user.attributes?.['custom:subscription_id'] && (
                      <div className="user-detail__info-item">
                        <label>Subscription ID</label>
                        <span className="user-detail__mono">...{user.attributes['custom:subscription_id'].slice(-8)}</span>
                      </div>
                    )}
                    {isSuperAdmin && user.attributes?.['custom:payment_method_id'] && (
                      <div className="user-detail__info-item">
                        <label>Payment Method</label>
                        <span className="user-detail__mono">...{user.attributes['custom:payment_method_id'].slice(-8)}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Family — highlight if family account missing family_id or group */}
            {(() => {
              const acctType = user.attributes?.['custom:account_type'] || '';
              const isFamily = acctType === 'family_parent' || acctType === 'parent' || acctType === 'child' || (user.attributes?.['custom:tier'] === 'family');
              if (!isFamily) return null;
              const hasFamilyId = !!user.attributes?.['custom:family_id'];
              const hasFamilyGroup = user.groups?.some(g => g.startsWith('family-'));
              const familyStuck = !hasFamilyId || !hasFamilyGroup;
              return (
                <div className="admin-card" style={familyStuck ? { border: '2px solid #F59E0B' } : {}}>
                  <div className="admin-card-header">
                    <h3 className="admin-card-title">👨‍👩‍👧‍👦 Family</h3>
                    {familyStuck && <StatusBadge text="Incomplete" variant="warning" />}
                  </div>
                  <div className="user-detail__info-grid">
                    <div className="user-detail__info-item" style={!hasFamilyId ? { border: '1px solid #F59E0B', background: '#FFFBEB' } : {}}>
                      <label>Family ID</label>
                      <span className="user-detail__mono">{user.attributes?.['custom:family_id'] || 'Not set'}</span>
                    </div>
                    {acctType === 'child' && user.attributes?.['custom:parent_account_id'] && (
                      <div className="user-detail__info-item">
                        <label>Parent Account</label>
                        <span className="user-detail__mono">{user.attributes['custom:parent_account_id'].slice(0, 8)}...</span>
                      </div>
                    )}
                    {user.attributes?.['custom:student_count'] && user.attributes['custom:student_count'] !== '0' && (
                      <div className="user-detail__info-item">
                        <label>Student Count</label>
                        <span>{user.attributes['custom:student_count']}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Learning Profile — shown for students or anyone who filled it */}
            {(user.attributes?.['custom:age'] || user.attributes?.['custom:grade_level'] || user.attributes?.['custom:education_system'] || user.attributes?.['custom:account_type'] === 'child') && (
              <div className="admin-card">
                <div className="admin-card-header">
                  <h3 className="admin-card-title">📚 Learning Profile</h3>
                </div>
                <div className="user-detail__info-grid">
                  {user.attributes?.['custom:age'] && (
                    <div className="user-detail__info-item"><label>Age</label><span>{user.attributes['custom:age']}</span></div>
                  )}
                  {user.attributes?.['custom:grade_level'] && (
                    <div className="user-detail__info-item"><label>Grade Level</label><span>{user.attributes['custom:grade_level']}</span></div>
                  )}
                  {user.attributes?.['custom:education_system'] && (
                    <div className="user-detail__info-item"><label>Education System</label><span>{user.attributes['custom:education_system']}</span></div>
                  )}
                  {user.attributes?.['custom:learning_style'] && (
                    <div className="user-detail__info-item"><label>Learning Style</label><span>{user.attributes['custom:learning_style']}</span></div>
                  )}
                  {user.attributes?.birthdate && (
                    <div className="user-detail__info-item"><label>Birthdate</label><span>{user.attributes.birthdate}</span></div>
                  )}
                </div>
              </div>
            )}

            {/* COPPA — only for under-13 or if consent data exists */}
            {((user.attributes?.['custom:age'] && parseInt(user.attributes['custom:age']) < 13) || user.attributes?.['custom:parental_consent']) && (
              <div className="admin-card">
                <div className="admin-card-header">
                  <h3 className="admin-card-title">🛡️ COPPA Compliance</h3>
                </div>
                <div className="user-detail__info-grid">
                  <div className="user-detail__info-item">
                    <label>Parental Consent</label>
                    <span>{user.attributes?.['custom:parental_consent'] === 'true' ? '✅ Yes' : '❌ No'}</span>
                  </div>
                  {user.attributes?.['custom:parental_consent_date'] && (
                    <div className="user-detail__info-item"><label>Consent Date</label><span>{formatDate(user.attributes['custom:parental_consent_date'])}</span></div>
                  )}
                  {isSuperAdmin && user.attributes?.['custom:parent_email'] && (
                    <div className="user-detail__info-item"><label>Parent Email</label><span>{user.attributes['custom:parent_email']}</span></div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="admin-card">
            <div className="admin-card-header">
              <h3 className="admin-card-title">Uploaded Documents ({documents.length})</h3>
            </div>
            {documents.length > 0 ? (
              <div className="user-detail__documents">
                {documents.map((doc, index) => (
                  <div key={doc.documentId || index}>
                    <div 
                      className="user-detail__document-item" 
                      style={{ cursor: 'pointer' }}
                      onClick={() => setExpandedDocId(expandedDocId === (doc.documentId || index) ? null : (doc.documentId || index))}
                    >
                      <span className="user-detail__document-icon">
                        {doc.documentType === 'worksheet' ? '📝' : doc.documentType === 'exam' || doc.documentType === 'quiz' ? '📋' : '📄'}
                      </span>
                      <div className="user-detail__document-info">
                        <div className="user-detail__document-name">
                          {doc.fileName || 'Untitled Document'}
                        </div>
                        <div className="user-detail__document-meta">
                          {doc.subject !== 'unknown' && <span>{doc.subject}</span>}
                          {doc.grade !== 'unknown' && <span> • {doc.grade}</span>}
                          {doc.language && doc.language !== 'en' && <span> • {doc.language}</span>}
                          {doc.score && <span> • Score: {doc.score.correct ?? '?'}/{doc.score.total ?? '?'}</span>}
                          <span> • {formatDate(doc.uploadedAt)}</span>
                          {doc.parsedText && <span> • 📖</span>}
                        </div>
                      </div>
                      <span style={{ fontSize: '0.75rem', color: '#9CA3AF', marginLeft: 'auto' }}>
                        {expandedDocId === (doc.documentId || index) ? '▲' : '▼'}
                      </span>
                    </div>
                    {expandedDocId === (doc.documentId || index) && (
                      <div style={{ 
                        padding: '0.75rem 1rem', 
                        background: '#F9FAFB', 
                        borderRadius: '0 0 0.5rem 0.5rem',
                        borderTop: '1px solid #E5E7EB',
                        marginTop: '-0.25rem',
                        marginBottom: '0.5rem'
                      }}>
                        {doc.parsedText ? (
                          <div>
                            <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
                              Extracted Content
                            </div>
                            <pre style={{ 
                              fontSize: '0.8rem', 
                              whiteSpace: 'pre-wrap', 
                              wordBreak: 'break-word',
                              background: '#FFFFFF',
                              border: '1px solid #E5E7EB',
                              borderRadius: '0.375rem',
                              padding: '0.75rem',
                              maxHeight: '400px',
                              overflow: 'auto',
                              fontFamily: 'inherit',
                              lineHeight: 1.5,
                              color: '#374151'
                            }}>
                              {doc.parsedText}
                            </pre>
                          </div>
                        ) : (
                          <div style={{ fontSize: '0.8rem', color: '#9CA3AF', fontStyle: 'italic' }}>
                            No extracted content available
                          </div>
                        )}
                        {doc.tags?.length > 0 && (
                          <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                            {doc.tags.map((tag, i) => (
                              <span key={i} style={{ fontSize: '0.7rem', padding: '0.125rem 0.5rem', background: '#E5E7EB', borderRadius: '1rem', color: '#4B5563' }}>{tag}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="admin-empty">
                <div className="admin-empty-icon">📁</div>
                <div className="admin-empty-title">No documents</div>
                <div className="admin-empty-text">User hasn't uploaded any documents yet.</div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'conversations' && (
          <div style={{ display: 'flex', gap: '1rem', height: 'calc(100vh - 320px)', minHeight: '400px' }}>
            {/* Conversation List (sidebar) */}
            <div className="admin-card" style={{ width: '340px', flexShrink: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <div className="admin-card-header" style={{ flexShrink: 0 }}>
                <h3 className="admin-card-title">Conversations ({chatHistory.length})</h3>
              </div>
              {chatHistory.length > 0 ? (
                <div style={{ overflow: 'auto', flex: 1 }}>
                  {chatHistory.map((conv, index) => {
                    const isSelected = selectedConvId === (conv.id || index);
                    return (
                      <div
                        key={conv.id || index}
                        onClick={() => setSelectedConvId(conv.id || index)}
                        style={{
                          padding: '0.75rem 1rem',
                          cursor: 'pointer',
                          borderBottom: '1px solid #E5E7EB',
                          background: isSelected ? '#EFF6FF' : 'transparent',
                          borderLeft: isSelected ? '3px solid #0066FF' : '3px solid transparent',
                          transition: 'background 0.15s'
                        }}
                      >
                        <div style={{ fontSize: '0.85rem', fontWeight: isSelected ? 600 : 500, color: '#111827', marginBottom: '0.25rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {conv.title || 'Untitled Conversation'}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#6B7280', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <span>{conv.messageCount || 0} msgs</span>
                          {conv.sessionType !== 'GENERAL' && <span>• {conv.sessionType}</span>}
                          <span>• {formatDate(conv.createdAt)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="admin-empty" style={{ flex: 1 }}>
                  <div className="admin-empty-icon">💬</div>
                  <div className="admin-empty-title">No conversations</div>
                </div>
              )}
            </div>

            {/* Message Viewer (main area) */}
            <div className="admin-card" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {(() => {
                const selectedConv = chatHistory.find(c => c.id === selectedConvId);
                if (!selectedConv) {
                  return (
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF' }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>💬</div>
                        <div>Select a conversation to view messages</div>
                      </div>
                    </div>
                  );
                }
                const messages = selectedConv.messages || [];
                return (
                  <>
                    <div className="admin-card-header" style={{ flexShrink: 0, borderBottom: '1px solid #E5E7EB' }}>
                      <div>
                        <h3 className="admin-card-title" style={{ marginBottom: '0.25rem' }}>{selectedConv.title || 'Untitled'}</h3>
                        <div style={{ fontSize: '0.7rem', color: '#6B7280' }}>
                          {selectedConv.sessionType} • {selectedConv.mode} • {messages.length} messages • {formatDate(selectedConv.createdAt)}
                          {selectedConv.referenceCode && ` • ${selectedConv.referenceCode}`}
                        </div>
                      </div>
                    </div>
                    <div style={{ flex: 1, overflow: 'auto', padding: '1rem' }}>
                      {messages.length > 0 ? messages.map((msg, i) => {
                        const isUser = msg.role === 'user' || msg.sender === 'user';
                        const isSystem = msg.role === 'system';
                        if (isSystem) return null;
                        return (
                          <div key={i} style={{
                            display: 'flex',
                            justifyContent: isUser ? 'flex-end' : 'flex-start',
                            marginBottom: '0.75rem'
                          }}>
                            <div style={{
                              maxWidth: '75%',
                              padding: '0.625rem 0.875rem',
                              borderRadius: isUser ? '1rem 1rem 0.25rem 1rem' : '1rem 1rem 1rem 0.25rem',
                              background: isUser ? '#0066FF' : '#F3F4F6',
                              color: isUser ? '#FFFFFF' : '#111827',
                              fontSize: '0.85rem',
                              lineHeight: 1.5,
                              wordBreak: 'break-word',
                              whiteSpace: 'pre-wrap'
                            }}>
                              {msg.content || msg.text || ''}
                              {msg.timestamp && (
                                <div style={{ fontSize: '0.6rem', marginTop: '0.25rem', opacity: 0.6, textAlign: 'right' }}>
                                  {new Date(msg.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      }) : (
                        <div style={{ textAlign: 'center', color: '#9CA3AF', padding: '2rem' }}>
                          No messages in this conversation
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {activeTab === 'mastery' && (
          <div className="admin-card">
            <div className="admin-card-header">
              <h3 className="admin-card-title">Mastery Tracking Data</h3>
            </div>
            {masteryData ? (
              <div className="user-detail__mastery">
                <div className="user-detail__mastery-stats">
                  <div className="user-detail__mastery-stat">
                    <label>Total Topics</label>
                    <span>{masteryData.totalTopics || 0}</span>
                  </div>
                  <div className="user-detail__mastery-stat">
                    <label>Mastered</label>
                    <span>{masteryData.masteredTopics || 0}</span>
                  </div>
                  <div className="user-detail__mastery-stat">
                    <label>In Progress</label>
                    <span>{masteryData.inProgressTopics || 0}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="admin-empty">
                <div className="admin-empty-icon">📊</div>
                <div className="admin-empty-title">No mastery data</div>
                <div className="admin-empty-text">User hasn't started learning yet.</div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'subscription' && (
          <>
            <SubscriptionEndDatePanel
              user={user}
              isSuperAdmin={isSuperAdmin}
            />
            <UpgradeHistoryPanel user={user} />
          </>
        )}

        {activeTab === 'promo-codes' && (
          <div className="admin-card">
            <div className="admin-card-header">
              <h3 className="admin-card-title">🎟️ Promo Codes Used ({promoCodes.length})</h3>
            </div>
            {promoCodes.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #E5E7EB', textAlign: 'left' }}>
                      <th style={{ padding: '12px', fontWeight: 600, color: '#374151' }}>Code</th>
                      <th style={{ padding: '12px', fontWeight: 600, color: '#374151' }}>Type</th>
                      <th style={{ padding: '12px', fontWeight: 600, color: '#374151' }}>Status</th>
                      <th style={{ padding: '12px', fontWeight: 600, color: '#374151' }}>Redeemed At</th>
                      <th style={{ padding: '12px', fontWeight: 600, color: '#374151' }}>Plan Selected</th>
                      <th style={{ padding: '12px', fontWeight: 600, color: '#374151' }}>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {promoCodes.map((pc, index) => {
                      const redeemer = (Array.isArray(pc.redeemedBy) ? pc.redeemedBy : []).find(
                        r => r.userEmail === user.email || r.userId === userId
                      );
                      return (
                        <tr key={pc.code || index} style={{ borderBottom: '1px solid #E5E7EB' }}>
                          <td style={{ padding: '12px', fontFamily: 'monospace', fontWeight: 500 }}>
                            {pc.code}
                          </td>
                          <td style={{ padding: '12px' }}>
                            <span style={{
                              padding: '0.25rem 0.5rem',
                              borderRadius: '0.25rem',
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              background: '#DBEAFE',
                              color: '#1E40AF'
                            }}>
                              {pc.promoType}
                            </span>
                            {pc.promoType === 'trial_extension' && pc.extensionDays && (
                              <span style={{ marginLeft: '0.25rem', color: '#6B7280', fontSize: '0.75rem' }}>
                                (+{pc.extensionDays}d)
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '12px' }}>
                            <span style={{
                              padding: '0.25rem 0.5rem',
                              borderRadius: '0.25rem',
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              background: pc.status === 'active' ? '#D1FAE5' :
                                         pc.status === 'redeemed' ? '#E5E7EB' :
                                         pc.status === 'revoked' ? '#FEE2E2' : '#FEF3C7',
                              color: pc.status === 'active' ? '#065F46' :
                                     pc.status === 'redeemed' ? '#374151' :
                                     pc.status === 'revoked' ? '#991B1B' : '#92400E'
                            }}>
                              {pc.status}
                            </span>
                          </td>
                          <td style={{ padding: '12px', color: '#6B7280' }}>
                            {redeemer?.redeemedAt ? formatDate(redeemer.redeemedAt) : '—'}
                          </td>
                          <td style={{ padding: '12px', color: '#6B7280' }}>
                            {redeemer?.planSelected || '—'}
                          </td>
                          <td style={{ padding: '12px', color: '#6B7280', maxWidth: '200px' }}>
                            {pc.notes ? (
                              <span title={pc.notes} style={{ cursor: 'help' }}>
                                {pc.notes.length > 30 ? pc.notes.slice(0, 30) + '...' : pc.notes}
                              </span>
                            ) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="admin-empty">
                <div className="admin-empty-icon">🎟️</div>
                <div className="admin-empty-title">No promo codes used</div>
                <div className="admin-empty-text">This user has not redeemed any promo codes.</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* SuperAdmin Actions */}
      {isSuperAdmin && (
        <div className="admin-card">
          <div className="admin-card-header">
            <h3 className="admin-card-title">Admin Actions</h3>
          </div>
          <div className="user-detail__actions">
            {user.enabled ? (
              <button
                onClick={() => setShowDisableModal(true)}
                className="admin-btn admin-btn--danger"
              >
                🚫 Disable User
              </button>
            ) : (
              <button
                onClick={() => setShowEnableModal(true)}
                className="admin-btn admin-btn--success"
              >
                ✅ Enable User
              </button>
            )}
            <button
              onClick={() => setShowResetModal(true)}
              className="admin-btn admin-btn--warning"
              disabled={!user.enabled}
            >
              🔑 Reset Password
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      {showEditModal && (
        <EditUserModal
          user={user}
          onSave={handleUpdateUser}
          onClose={() => setShowEditModal(false)}
          loading={actionLoading}
        />
      )}

      {showDisableModal && (
        <DisableUserModal
          user={user}
          onConfirm={handleDisableUser}
          onClose={() => setShowDisableModal(false)}
          loading={actionLoading}
        />
      )}

      {showEnableModal && (
        <Modal
          title="Enable User"
          onClose={() => setShowEnableModal(false)}
          footer={
            <>
              <button onClick={() => setShowEnableModal(false)} className="admin-btn admin-btn--secondary">
                Cancel
              </button>
              <button onClick={handleEnableUser} className="admin-btn admin-btn--success" disabled={actionLoading}>
                {actionLoading ? 'Enabling...' : 'Enable User'}
              </button>
            </>
          }
        >
          <p>Are you sure you want to enable {user.email}? They will be able to log in again.</p>
        </Modal>
      )}

      {showResetModal && (
        <Modal
          title="Reset Password"
          onClose={() => setShowResetModal(false)}
          footer={
            <>
              <button onClick={() => setShowResetModal(false)} className="admin-btn admin-btn--secondary">
                Cancel
              </button>
              <button onClick={handleResetPassword} className="admin-btn admin-btn--warning" disabled={actionLoading}>
                {actionLoading ? 'Sending...' : 'Send Reset Email'}
              </button>
            </>
          }
        >
          <p>Send a password reset email to {user.email}?</p>
        </Modal>
      )}
    </div>
  );
};

// Edit User Modal
const EditUserModal = ({ user, onSave, onClose, loading }) => {
  const [name, setName] = useState(user.name || '');
  const [familyName, setFamilyName] = useState(user.familyName || '');
  const [phoneNumber, setPhoneNumber] = useState(user.phoneNumber || '');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ name, familyName, phoneNumber });
  };

  return (
    <Modal
      title="Edit User"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="admin-btn admin-btn--secondary">
            Cancel
          </button>
          <button onClick={handleSubmit} className="admin-btn admin-btn--primary" disabled={loading}>
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        <div className="admin-form-group">
          <label>Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="First name"
            className="admin-input"
          />
        </div>
        <div className="admin-form-group">
          <label>Family Name</label>
          <input
            type="text"
            value={familyName}
            onChange={(e) => setFamilyName(e.target.value)}
            placeholder="Last name"
            className="admin-input"
          />
        </div>
        <div className="admin-form-group">
          <label>Phone Number</label>
          <input
            type="tel"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="+1234567890"
            className="admin-input"
          />
        </div>
      </form>
    </Modal>
  );
};

// Disable User Modal
const DisableUserModal = ({ user, onConfirm, onClose, loading }) => {
  const [reason, setReason] = useState('');

  return (
    <Modal
      title="Disable User"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="admin-btn admin-btn--secondary">
            Cancel
          </button>
          <button
            onClick={() => onConfirm(reason)}
            className="admin-btn admin-btn--danger"
            disabled={loading}
          >
            {loading ? 'Disabling...' : 'Disable User'}
          </button>
        </>
      }
    >
      <p>Are you sure you want to disable {user.email}? They will not be able to log in.</p>
      <div className="admin-form-group admin-section-gap">
        <label>Reason (optional)</label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Enter reason for disabling..."
          rows={3}
          className="admin-input"
        />
      </div>
    </Modal>
  );
};

export default UserDetail;
